// RM Grade Stock — Feb 27, 2026.
//
// Grade-level opening / received / issued / closing ledger. Seeded from
// BOM Master (every {grade, material_type} pair). While "Run" is active
// the row's opening balance is locked and closing = opening + received −
// issued is computed live. Receive / Issue happen through a small modal
// so we don't accidentally clobber values on tab-out.

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Play, Pause, Plus, Minus, MagnifyingGlass, DownloadSimple,
  UploadSimple, FileText, PencilSimple, ArrowsClockwise, LockKey, Package,
} from "@phosphor-icons/react";

const f2 = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RMGradeStock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState({ running: false, run_started_at: null });
  const [openingModal, setOpeningModal] = useState(null);         // {grade, material_type, opening_qty}
  const [moveModal, setMoveModal]       = useState(null);         // {grade, material_type, kind}
  const [openingValue, setOpeningValue] = useState("");
  const [moveValue, setMoveValue]       = useState("");
  const [moveNote, setMoveNote]         = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/rm-grade-stock`, { skipPeriod: true }),
        axios.get(`${API}/rm-grade-stock/status`, { skipPeriod: true }),
      ]);
      setRows(r.data || []);
      setStatus(s.data || { running: false });
    } catch { toast.error("Failed to load RM grade stock"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll every 30s while running so multi-user updates surface without
  // requiring a manual Refresh. Cheap: one small GET per interval.
  useEffect(() => {
    if (!status.running) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [status.running, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.grade || "").toLowerCase().includes(q) ||
      (r.material_type || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totals = useMemo(() => filtered.reduce((a, r) => ({
    opening:  a.opening  + Number(r.opening_qty  || 0),
    received: a.received + Number(r.received_qty || 0),
    issued:   a.issued   + Number(r.issued_qty   || 0),
    closing:  a.closing  + Number(r.closing_qty  || 0),
  }), { opening: 0, received: 0, issued: 0, closing: 0 }), [filtered]);

  const startRun = async () => {
    if (!window.confirm(
      "Start the RM ledger run?\n\n" +
      "This locks every grade's opening balance and zeroes the receive/issue counters. " +
      "Closing = Opening + Received − Issued will compute live from now on."
    )) return;
    try {
      const r = await axios.post(`${API}/rm-grade-stock/run`);
      toast.success(`Started run — ${r.data.started} grade(s) locked`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
  };

  const stopRun = async () => {
    if (!window.confirm("Stop the run? Opening balance becomes editable again — received / issued totals stay for review.")) return;
    try {
      await axios.post(`${API}/rm-grade-stock/stop`);
      toast.success("Run stopped");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Stop failed"); }
  };

  const openOpeningModal = (r) => {
    setOpeningModal(r);
    setOpeningValue(String(r.opening_qty || 0));
  };
  const saveOpening = async () => {
    if (!openingModal) return;
    const qty = Number(openingValue);
    if (Number.isNaN(qty) || qty < 0) { toast.error("Enter a non-negative number"); return; }
    try {
      await axios.put(`${API}/rm-grade-stock/opening`, {
        grade: openingModal.grade,
        material_type: openingModal.material_type,
        opening_qty: qty,
      });
      toast.success(`Opening for ${openingModal.grade} saved`);
      setOpeningModal(null); setOpeningValue("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openMoveModal = (r, kind) => {
    setMoveModal({ ...r, kind });
    setMoveValue(""); setMoveNote("");
  };
  const saveMovement = async () => {
    if (!moveModal) return;
    const qty = Number(moveValue);
    if (Number.isNaN(qty) || qty <= 0) { toast.error("Enter qty > 0"); return; }
    try {
      await axios.post(`${API}/rm-grade-stock/movement`, {
        grade: moveModal.grade,
        material_type: moveModal.material_type,
        kind: moveModal.kind,
        qty,
        note: moveNote,
      });
      toast.success(`${moveModal.kind === "receive" ? "Received" : "Issued"} ${qty}Kg`);
      setMoveModal(null); setMoveValue(""); setMoveNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/rm-grade-stock/template`, {
        responseType: "blob", skipPeriod: true,
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "rm-grade-stock-template.xlsx";
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const exportExcel = async () => {
    try {
      const r = await axios.get(`${API}/rm-grade-stock/export`, {
        responseType: "blob", skipPeriod: true,
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `rm-grade-stock_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const importExcel = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/rm-grade-stock/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, skipPeriod: true,
      });
      const errCount = (r.data.errors || []).length;
      toast.success(`Imported ${r.data.saved || 0} rows${errCount ? ` · ${errCount} error(s)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Layout title="RM Grade Stock">
      <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]">
              <Package size={20} weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{ fontFamily: "Chivo" }}>
                RM Grade Stock
              </h1>
              <p className="text-xs md:text-sm text-[#64748B] mt-0.5">
                Per-grade opening / received / issued ledger. Grades are seeded from BOM Master.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {status.running ? (
              <Button onClick={stopRun} className="bg-[#B45309] hover:bg-[#92400E] text-white h-9" data-testid="rmgs-stop-btn">
                <Pause size={14} className="mr-1" weight="fill" /> Stop
              </Button>
            ) : (
              <Button onClick={startRun} className="bg-[#166534] hover:bg-[#14532D] text-white h-9" data-testid="rmgs-run-btn">
                <Play size={14} className="mr-1" weight="fill" /> Run
              </Button>
            )}
            <Button variant="outline" onClick={load} className="h-9" data-testid="rmgs-refresh">
              <ArrowsClockwise size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Status ribbon */}
        <div className={`p-3 rounded-lg border-2 ${status.running ? "bg-[#DCFCE7] border-[#166534]" : "bg-[#F1F5F9] border-[#CBD5E1]"}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest px-2 py-1 rounded ${status.running ? "bg-[#166534] text-white" : "bg-[#64748B] text-white"}`}>
              {status.running ? <Play size={11} weight="fill" /> : <Pause size={11} weight="fill" />}
              {status.running ? "RUNNING" : "STOPPED"}
            </span>
            {status.running && status.run_started_at && (
              <span className="text-xs text-[#166534] font-semibold">
                Started {new Date(status.run_started_at).toLocaleString()}
              </span>
            )}
            {status.running ? (
              <span className="text-xs text-[#166534]">
                Opening balances are <b>locked</b>. Use +/− to record receive / issue.
              </span>
            ) : (
              <span className="text-xs text-[#64748B]">
                Press <b>Run</b> to lock openings and start live closing calculation.
              </span>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <Card className="p-3 border border-[#E2E8F0]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search grade or material type…"
                className="h-9 pl-9" data-testid="rmgs-search" />
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="h-9" data-testid="rmgs-template">
              <FileText size={14} className="mr-1" /> Template
            </Button>
            <Button variant="outline" onClick={exportExcel} className="h-9" data-testid="rmgs-export">
              <DownloadSimple size={14} className="mr-1" /> Export
            </Button>
            <input type="file" ref={fileRef} accept=".xlsx,.xls"
              onChange={e => importExcel(e.target.files?.[0])} className="hidden" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-9" data-testid="rmgs-import">
              <UploadSimple size={14} className="mr-1" /> Import
            </Button>
            <span className="text-[11px] text-[#64748B] font-semibold pr-2">
              {filtered.length} / {rows.length} grades
            </span>
          </div>
        </Card>

        {/* Totals cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TotalCard label="Opening"  value={totals.opening}  tint="#1D2951" />
          <TotalCard label="Received" value={totals.received} tint="#166534" />
          <TotalCard label="Issued"   value={totals.issued}   tint="#B91C1C" />
          <TotalCard label="Closing"  value={totals.closing}  tint="#D4A843" bold />
        </div>

        {/* Table */}
        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">Grade</th>
                  <th className="px-3 py-3 text-left font-semibold">Material Type</th>
                  <th className="px-3 py-3 text-right font-semibold">Opening (Kg)</th>
                  <th className="px-3 py-3 text-right font-semibold">Received (Kg)</th>
                  <th className="px-3 py-3 text-right font-semibold">Issued (Kg)</th>
                  <th className="px-3 py-3 text-right font-semibold">Closing (Kg)</th>
                  <th className="px-3 py-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={7} className="py-12 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="py-16 text-center text-[#94A3B8]">
                    {rows.length === 0
                      ? <>No grades in BOM Master yet. Add grades in <b>BOM Master</b> first.</>
                      : "No grade matches your search."}
                  </td></tr>
                )}
                {filtered.map((r) => {
                  const key = `${r.grade}::${r.material_type}`;
                  return (
                    <tr key={key} className={`hover:bg-[#F8FAFC] ${r.orphan ? "bg-[#FEF3C7]/30" : ""}`} data-testid={`rmgs-row-${key}`}>
                      <td className="px-3 py-2 font-mono font-bold text-[#1D2951]">
                        {r.grade}
                        {r.orphan && <span className="ml-2 text-[10px] font-bold text-[#B45309]" title="Grade removed from BOM Master">(orphan)</span>}
                      </td>
                      <td className="px-3 py-2 capitalize text-[#334155]">{r.material_type || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className="inline-flex items-center gap-1">
                          {r.running && <LockKey size={10} weight="fill" className="text-[#94A3B8]" />}
                          {f2(r.opening_qty)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#166534]">{f2(r.received_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#B91C1C]">{f2(r.issued_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono font-black text-[#0F172A]">{f2(r.closing_qty)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openOpeningModal(r)}
                            disabled={r.running}
                            className={`w-8 h-8 flex items-center justify-center rounded-md border transition ${r.running ? "border-[#E2E8F0] text-[#CBD5E1] cursor-not-allowed" : "border-[#E2E8F0] text-[#334155] hover:border-[#1D2951] hover:text-[#1D2951]"}`}
                            title={r.running ? "Locked while running" : "Edit opening balance"}
                            data-testid={`rmgs-edit-${key}`}>
                            <PencilSimple size={13} />
                          </button>
                          <button
                            onClick={() => openMoveModal(r, "receive")}
                            disabled={!r.running}
                            className={`w-8 h-8 flex items-center justify-center rounded-md border transition ${r.running ? "border-[#166534] text-[#166534] hover:bg-[#166534] hover:text-white" : "border-[#E2E8F0] text-[#CBD5E1] cursor-not-allowed"}`}
                            title={r.running ? "Add receive" : "Press Run first"}
                            data-testid={`rmgs-add-${key}`}>
                            <Plus size={13} weight="bold" />
                          </button>
                          <button
                            onClick={() => openMoveModal(r, "issue")}
                            disabled={!r.running}
                            className={`w-8 h-8 flex items-center justify-center rounded-md border transition ${r.running ? "border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white" : "border-[#E2E8F0] text-[#CBD5E1] cursor-not-allowed"}`}
                            title={r.running ? "Record issue" : "Press Run first"}
                            data-testid={`rmgs-sub-${key}`}>
                            <Minus size={13} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Opening balance modal */}
      <Dialog open={!!openingModal} onOpenChange={(o) => { if (!o) setOpeningModal(null); }}>
        <DialogContent className="max-w-md" data-testid="rmgs-opening-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#1D2951]">
              Set Opening — {openingModal?.grade}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Grade / Material">
              <Input value={`${openingModal?.grade || ""} · ${openingModal?.material_type || ""}`}
                readOnly className="h-9 bg-[#F1F5F9]" />
            </Field>
            <Field label="Opening Qty (Kg) *">
              <Input type="number" step="0.001" value={openingValue}
                onChange={e => setOpeningValue(e.target.value)}
                className="h-9 font-mono" autoFocus
                data-testid="rmgs-opening-value" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpeningModal(null)}>Cancel</Button>
            <Button onClick={saveOpening} className="bg-[#1D2951] hover:bg-[#2A3B73]" data-testid="rmgs-opening-save">
              Save Opening
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement modal (receive / issue) */}
      <Dialog open={!!moveModal} onOpenChange={(o) => { if (!o) setMoveModal(null); }}>
        <DialogContent className="max-w-md" data-testid="rmgs-move-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#1D2951] capitalize">
              {moveModal?.kind === "receive" ? "Add Receive" : "Record Issue"} — {moveModal?.grade}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Grade / Material">
              <Input value={`${moveModal?.grade || ""} · ${moveModal?.material_type || ""}`}
                readOnly className="h-9 bg-[#F1F5F9]" />
            </Field>
            <Field label={`Qty (Kg) *`}>
              <Input type="number" step="0.001" value={moveValue}
                onChange={e => setMoveValue(e.target.value)}
                className="h-9 font-mono" autoFocus
                data-testid="rmgs-move-qty" />
            </Field>
            <Field label="Note (optional)">
              <Input value={moveNote}
                onChange={e => setMoveNote(e.target.value)}
                placeholder="Invoice ref, cutting order, …"
                className="h-9" data-testid="rmgs-move-note" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveModal(null)}>Cancel</Button>
            <Button onClick={saveMovement}
              className={moveModal?.kind === "receive" ? "bg-[#166534] hover:bg-[#14532D]" : "bg-[#B91C1C] hover:bg-[#7F1D1D]"}
              data-testid="rmgs-move-save">
              {moveModal?.kind === "receive" ? "Add Receive" : "Record Issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function TotalCard({ label, value, tint, bold }) {
  return (
    <Card className="p-3 border border-[#E2E8F0]">
      <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: tint }}>
        {label} (Kg)
      </div>
      <div className={`mt-1 text-xl md:text-2xl font-mono ${bold ? "font-black text-[#0F172A]" : "font-bold text-[#334155]"}`}>
        {f2(value)}
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-widest text-[#64748B]">{label}</Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
